import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { addDays, isBefore } from 'date-fns';
import { map, Observable, of, tap } from 'rxjs';
import { ContextService } from './context.service';
import { SessionStorageService } from './session-storage.service';

interface AvatarSessionItem {
  image: string;
  expiration: string;
}

interface PhotoUpload {
  key: string;
  photoUrl: string | null;
}

@Injectable()
export class AvatarService {
  constructor(
    private readonly http: HttpClient,
    private readonly context: ContextService,
    private readonly storage: SessionStorageService,
  ) {}

  get(key: string, useCache: boolean = true): Observable<string> {
    const cached = this.fromSession(key);
    if (cached && useCache) {
      return of(`url(data:image/png;base64,${cached})`);
    }
    const url = `${this.context.website}/mvc/AddressBook/ViewDetails/GetPhoto/${key}${useCache ? '' : '?refresh=true'}`;
    return this.http.get(url, { responseType: 'text' }).pipe(
      tap((image) => this.addToSession(key, image)),
      map((image) => (image ? `url(data:image/png;base64,${image})` : '')),
    );
  }

  set(photo: PhotoUpload): Observable<boolean> {
    return this.http
      .post<{
        Code: number;
        errorMessage: string;
      }>(
        `${this.context.website}/mvc/AddressBook/EditDetails/UploadPhoto`,
        photo,
      )
      .pipe(
        map((result) => {
          if (result.Code === 0) {
            return true;
          }
          return false;
        }),
      );
  }

  delete(key: string): Observable<boolean> {
    return this.http
      .get<{
        Code: number;
        errorMessage: string;
      }>(
        `${this.context.website}/mvc/AddressBook/ViewDetails/PhotoDeletion/${key}`,
      )
      .pipe(
        map((result) => {
          if (result.Code === 0) {
            this.removeFromSession(key);
            return true;
          }
          return false;
        }),
      );
  }

  private removeFromSession(key: string): void {
    try {
      if (this.storage.isEnabled) {
        const storageKey = key + '_photo';
        const item = this.storage.get(storageKey);
        if (item) {
          this.storage.remove(storageKey);
        }
      }
    } catch {
      // Storage operations may fail in certain environments (e.g., incognito mode)
      // Silently fail as avatar removal is not critical to application functionality
    }
  }

  private fromSession(key: string): string | null {
    try {
      if (this.storage.isEnabled) {
        const storageKey = key + '_photo';
        const item = this.storage.get(storageKey);
        if (item) {
          const data = JSON.parse(item) as AvatarSessionItem;
          if (data?.expiration) {
            if (isBefore(Date.now(), Date.parse(data.expiration))) {
              return data.image;
            } else {
              this.storage.remove(storageKey);
            }
          }
        }
      }
    } catch {
      return null;
    }
    return null;
  }

  private addToSession(key: string, image: string): void {
    if (this.storage.isEnabled) {
      const storageKey = key + '_photo';
      const expiration = addDays(Date.now(), 1);
      const data = {
        image,
        expiration,
      };
      this.storage.set(storageKey, JSON.stringify(data));
    }
  }
}
